import React, { useState, useContext, useRef } from 'react';
import styled from 'styled-components/macro';
import shuffle from 'lodash/shuffle';

import { fontDefault, fontkoKR } from '@mirinae/react-ui';
import { os } from '@mirinae/js-utils/utils/userAgent';
import GTM from '@mirinae/apis/GoogleAnalytics';
import { classes } from '@mirinae/react-ui';

import { lessonsPalette } from '@mirinae/lessons/modules/defines/styles';
import PlayerContext from '@mirinae/lessons/modules/contexts/player';
import Element from '@mirinae/lessons/components/lesson/elements/Element';
import { FormattedEnPhrase, FormattedKoPhrase } from '@mirinae/lessons/components/lesson/elements/Phrase';
import SuccessOverlay from '@mirinae/lessons/components/lesson/elements/shared/SuccessOverlay';

const Stage = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 40px;
  
    .${classes.mobile} & {
        flex-direction: row;
        align-items: center;
        gap: 40px;
        margin-bottom: 50px;
    }
`;

const ChoiceButton = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: ${props => props.width}px;
    height: 90px;
    background-color: ${props => (props.picked ? lessonsPalette.lessons[4] : lessonsPalette.primary.white)};
    border: 1px solid ${props => (props.picked ? lessonsPalette.darken[0] : lessonsPalette.grey[5])};
    box-sizing: border-box;
    box-shadow: ${props => (props.picked ? 'none' : '3px 3px 7px rgba(159, 188, 186, 0.4)')};
    border-radius: 10px;
    color: ${lessonsPalette.grey[0]};
    cursor: pointer;
    & span {
        color: inherit !important;
    }
    &:hover {
        background-color: ${lessonsPalette.lessons[4]};
        border-color: ${lessonsPalette.darken[0]};
        box-shadow: none;
    }
    visibility: ${props => props.visibility};
    // transition: visibility 250ms linear;
  
    .${classes.mobile} & {
        height: 57px;
    }
    
`;

const BadChoiceButton = styled(ChoiceButton)`
    border-color: ${lessonsPalette.lessons[2]};
    background-color: ${lessonsPalette.lessons[3]};
    animation: wiggle 0.25s;
    box-shadow: none;

    &:hover {
        border-color: ${lessonsPalette.lessons[2]};
        background-color: ${lessonsPalette.lessons[3]};
    }

    @keyframes wiggle {
        0% { transform: rotate(0deg); }
        5% { transform: rotate(-5deg); }
       80% { transform: rotate(5deg); }
      100% { transform: rotate(0deg); }
    }
`;

const CorrectChoiceButton = styled(ChoiceButton)`
    background: ${lessonsPalette.lessons[4]};
    border-color: ${lessonsPalette.primary.main};
    box-shadow: none;
`;

const HiddenButton = styled(ChoiceButton)`
    visibility: hidden;
`;

const ChoiceButtonLabel = styled.div`
    font-family: ${fontDefault};
    font-size: 22px;
    font-weight: 400;
`;

const Buttons = styled.div`
    display: flex;
    flex-direction: row;
    gap: 30px;
  
    .${classes.mobile} & {
        flex-direction: column;
        justify-content: center;
        gap: 30px;
    }
`;

const styles = {
    'ko': { koFontWeight: '600', koreanFontSize: '28px' },
    'en': { enFontWeight: '500', englishFontSize: '22px' },
};

const MatchTheLetters = ({ element }) => {
    const [matched, setMatched] = useState([]);
    const [firstPick, setFirstPick] = useState({});
    const [secondPick, setSecondPick] = useState({});
    const [badPick, setBadPick] = useState({});
    const [complete, setComplete] = useState(false);
    const matchedCount = useRef(0);
    const player = useContext(PlayerContext);

    const pairs = element.fields.filter(f => f.type === 'parts');
    const indices = [1, 2, 3, 4, 5, 6].slice(0, pairs.length);
    const [buttonMap] = useState({ 'ko': shuffle(indices), 'en': shuffle(indices) });

    const buttonClicked = (i, lang) => {
        setBadPick({});
        if (!firstPick.lang) {
            setFirstPick({ i, lang });
        } else if (i === firstPick.i && lang !== firstPick.lang) {
            // good match!
            setSecondPick({ i, lang });
            matchedCount.current += 1;
            if (matchedCount.current === pairs.length) {
                setComplete(true);
                player.postEvent({ action: 'complete' }, element);
                GTM.userAction('success', { page: player.currentPage.componentName }, 'lessons');
            }
            setTimeout(() => {
                matched[i] = true;
            }, 500);
            setTimeout(() => {
                setFirstPick({});
                setSecondPick({});
            }, 1000);
        } else {
            setBadPick({ i, lang });
            player.postEvent({ action: 'error' }, element);
            GTM.userAction('error', { page: player.currentPage.componentName }, 'lessons');
            setTimeout(() => {
                setBadPick({});
                setFirstPick({});
            }, 750);
        }
    };

    return (
        <Element element={element}>
            <Stage>
                { complete && <SuccessOverlay top={-35} /> }
                { ['en', 'ko'].map(lang => (
                    <Buttons key={lang} lang={lang} show={os.isOthers || !complete}>
                        { indices.map(index => {
                            const i = buttonMap[lang][index - 1];
                            let Button = badPick.i && (badPick.i === i && badPick.lang === lang || firstPick.i === i && firstPick.lang === lang) ? BadChoiceButton : ChoiceButton;
                            const Formatter = lang === 'ko' ? FormattedKoPhrase : FormattedEnPhrase;
                            return(
                                <Button key={i}
                                        picked={(lang === firstPick.lang && firstPick.i === i) || (secondPick.lang === lang && secondPick.i === i)}
                                        width={os.isOthers ? (pairs.length >= 6 ? 75 : 96) : 139}
                                        visibility={matched[i] ? 'hidden' : 'visible'} onClick={() => buttonClicked(i, lang)}
                                >
                                    <ChoiceButtonLabel>
                                        <Formatter phrase={pairs[i-1]} phraseStyle={styles[lang]} /></ChoiceButtonLabel>
                                </Button>
                            );
                        })}
                    </Buttons>
                ))}
            </Stage>
        </Element>
    );
};

export default MatchTheLetters;
